 /*!
 * TEMA DESENVOLVIDO POR SABINOv LTDA.
 * O USO INDEVIDO DO TEMA RESULTARÁ EM DMCA POR DIREITOS DO TEMA
 * @license Proprietary
 * @preserve
 */



import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('toggle', this.#handleExclusiveToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  #interactionId = 0;

  /**
   * Waits for the visual collapse of an accordion row to finish.
   * @param {HTMLDetailsElement} details
   * @returns {Promise<void>}
   */
  #waitForCollapse(details) {
    const content = details.querySelector('.details-content');

    if (!(content instanceof HTMLElement)) {
      return new Promise((resolve) => window.setTimeout(resolve, 350));
    }

    return new Promise((resolve) => {
      let finished = false;

      const finish = () => {
        if (finished) return;
        finished = true;
        content.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(fallbackTimer);
        resolve();
      };

      const onTransitionEnd = (transitionEvent) => {
        if (transitionEvent.target === content && ['block-size', 'opacity'].includes(transitionEvent.propertyName)) {
          finish();
        }
      };

      const fallbackTimer = window.setTimeout(finish, 500);
      content.addEventListener('transitionend', onTransitionEnd);
    });
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = async (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    const isOpening = !this.details.open;

    // Closing the currently open row does not require any scroll correction.
    if (!isOpening) return;

    const accordionGroup = this.closest('.accordion');
    if (!accordionGroup) return;

    event.preventDefault();

    const interactionId = ++this.#interactionId;
    const openRows = [...accordionGroup.querySelectorAll('accordion-custom > details[open]')].filter(
      (details) => details !== this.details
    );

    // Close the previous row first and let its CSS transition finish. Opening
    // and scrolling at the same time was what produced the visible up/down jump.
    for (const details of openRows) details.open = false;

    if (openRows.length > 0) {
      await Promise.all(openRows.map((details) => this.#waitForCollapse(details)));
    }

    if (interactionId !== this.#interactionId || !this.isConnected) return;

    this.details.open = true;

    // The heading does not move while its own content expands because that
    // content is below it. Therefore only one scroll command is necessary.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (interactionId !== this.#interactionId || !this.details.open) return;

        const topSpacing = 12;
        const targetTop = this.summary.getBoundingClientRect().top + window.scrollY - topSpacing;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    });
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Keeps only one row open inside the same accordion group.
   */
  #handleExclusiveToggle = () => {
    if (!this.details.open) return;

    const accordionGroup = this.closest('.accordion');
    if (!accordionGroup) return;

    accordionGroup.querySelectorAll('accordion-custom > details[open]').forEach((details) => {
      if (details !== this.details) details.open = false;
    });
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
